import type { SolverConfig, Street, TreeConfig } from '../types/solver'

export type BatchActionSet = 'strategic' | 'lite'

const DEFAULT_SIZES: Record<Street, number[]> = {
  flop: [33, 75],
  turn: [50, 75],
  river: [50, 100],
}

const legacySize = (tree: TreeConfig, street: Street): number => {
  if (street === 'flop') return tree.flopBetPercent
  if (street === 'turn') return tree.turnBetPercent
  return tree.riverBetPercent
}

export function normalizeBetSizes(values: number[] | undefined, fallback: number): number[] {
  const normalized = (values?.length ? values : [fallback])
    .map((value) => Math.round(Number(value)))
    .filter((value) => Number.isFinite(value) && value >= 10 && value <= 300)
  return [...new Set(normalized)].sort((a, b) => a - b).slice(0, 4)
}

export function getBetSizes(tree: TreeConfig, street: Street): number[] {
  const stored = tree.betSizes?.[street]
  const fallback = legacySize(tree, street) || DEFAULT_SIZES[street][0]
  return normalizeBetSizes(stored, fallback)
}

export function setBetSizes(tree: TreeConfig, street: Street, sizes: number[]): TreeConfig {
  const normalized = normalizeBetSizes(sizes, legacySize(tree, street))
  const next = {
    ...tree,
    betSizes: {
      flop: getBetSizes(tree, 'flop'),
      turn: getBetSizes(tree, 'turn'),
      river: getBetSizes(tree, 'river'),
      [street]: normalized,
    },
  }
  if (street === 'flop') next.flopBetPercent = normalized[0]
  if (street === 'turn') next.turnBetPercent = normalized[0]
  if (street === 'river') next.riverBetPercent = normalized[0]
  return next
}

export function betSizeString(tree: TreeConfig, street: Street): string {
  const sizes = getBetSizes(tree, street).map((value) => `${value}%`)
  if (tree.allIn) sizes.push('a')
  return sizes.join(',')
}

export function applyActionSet(config: SolverConfig, actionSet: BatchActionSet): SolverConfig {
  const next = structuredClone(config)
  if (actionSet === 'lite') {
    next.tree.betSizes = {
      flop: [next.tree.flopBetPercent],
      turn: [next.tree.turnBetPercent],
      river: [next.tree.riverBetPercent],
    }
    next.tree.raises = false
    next.tree.allIn = false
    return next
  }

  next.version = Math.max(2, next.version)
  next.tree.betSizes = structuredClone(DEFAULT_SIZES)
  next.tree.flopBetPercent = DEFAULT_SIZES.flop[0]
  next.tree.turnBetPercent = DEFAULT_SIZES.turn[0]
  next.tree.riverBetPercent = DEFAULT_SIZES.river[0]
  next.tree.raises = true
  next.tree.allIn = true
  next.memoryMode = 'compact'
  return next
}

export function createRecalculationConfig(config: SolverConfig): SolverConfig {
  const next = applyActionSet(config, 'strategic')
  next.id = crypto.randomUUID()
  next.createdAt = new Date().toISOString()
  next.trainingContext = config.trainingContext
    ? { ...config.trainingContext, batchId: crypto.randomUUID(), generated: false }
    : undefined
  return next
}

export function strategicChoiceLabel(tree: TreeConfig): string {
  const maximumSizes = Math.max(
    getBetSizes(tree, 'flop').length,
    getBetSizes(tree, 'turn').length,
    getBetSizes(tree, 'river').length,
  )
  const choices = 1 + maximumSizes + (tree.allIn ? 1 : 0)
  return `${choices}択想定 · ${tree.raises ? 'レイズあり' : 'レイズなし'}`
}
